import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './TestimonialsSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptyTestimonial = () => ({
  source: '',
  text: '',
  initial: '',
  image: '',
});

function TestimonialsSettings() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/testimonials`);
        if (Array.isArray(res.data?.testimonials)) {
          setItems(res.data.testimonials);
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, []);

  const handleChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAdd = () => {
    setItems((prev) => [...prev, emptyTestimonial()]);
  };

  const handleDelete = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/testimonials/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.url) {
        handleChange(index, 'image', response.data.url);
        setMessage('Image uploaded successfully.');
      }
    } catch (err) {
      console.error(err);
      setMessage('Unable to upload image. Please try again.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const res = await axios.put(`${API_BASE_URL}/api/testimonials`, items);
      if (Array.isArray(res.data?.testimonials)) {
        setItems(res.data.testimonials);
      }
      setMessage('Testimonials updated successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save testimonials. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p className="testimonials-settings-loading">Loading testimonials…</p>
    );
  }

  return (
    <div className="testimonials-settings">
      <div className="testimonials-settings-header">
        <div>
          <h2 className="testimonials-settings-title">Testimonials section</h2>
          <p className="testimonials-settings-subtitle">
            Edit client quotes shown on the home page. Use avatar image URL or
            upload; leave empty to show initials.
          </p>
        </div>
        <div className="testimonials-settings-actions">
          <button
            type="button"
            className="testimonials-settings-secondary-btn"
            onClick={handleAdd}
          >
            + Add testimonial
          </button>
          <button
            type="button"
            className="testimonials-settings-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="testimonials-settings-list">
        {items.map((item, index) => (
          <div key={index} className="testimonials-settings-row">
            <div className="testimonials-settings-col testimonials-settings-col-main">
              <label className="testimonials-settings-label">
                Source (e.g. client type)
                <input
                  type="text"
                  className="testimonials-settings-input"
                  value={item.source}
                  onChange={(e) => handleChange(index, 'source', e.target.value)}
                  placeholder="International Legal Partner"
                />
              </label>

              <label className="testimonials-settings-label">
                Quote text
                <textarea
                  className="testimonials-settings-textarea"
                  value={item.text}
                  onChange={(e) => handleChange(index, 'text', e.target.value)}
                  placeholder="Their seamless on-the-ground support..."
                  rows={3}
                />
              </label>

              <label className="testimonials-settings-label">
                Initials (shown if no image)
                <input
                  type="text"
                  className="testimonials-settings-input"
                  value={item.initial}
                  onChange={(e) =>
                    handleChange(index, 'initial', e.target.value)
                  }
                  placeholder="ILP"
                />
              </label>
            </div>

            <div className="testimonials-settings-col testimonials-settings-col-meta">
              <label className="testimonials-settings-label">
                Avatar image URL
                <input
                  type="text"
                  className="testimonials-settings-input"
                  value={item.image}
                  onChange={(e) =>
                    handleChange(index, 'image', e.target.value)
                  }
                  placeholder="https://example.com/avatar.jpg"
                />
              </label>

              <label className="testimonials-settings-label">
                Or upload image
                <input
                  type="file"
                  accept="image/*"
                  className="testimonials-settings-input"
                  onChange={(e) =>
                    handleImageUpload(index, e.target.files?.[0] || null)
                  }
                />
              </label>

              <button
                type="button"
                className="testimonials-settings-delete-btn"
                onClick={() => handleDelete(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <p className="testimonials-settings-empty">
            No testimonials yet. Click "Add testimonial" to create one.
          </p>
        )}
      </div>

      {message && (
        <p className="testimonials-settings-message">{message}</p>
      )}
    </div>
  );
}

export default TestimonialsSettings;
